import React, { useEffect, useState } from 'react';
import { formatReceiptTin } from 'app/utils/rut';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import Card from "@mui/material/Card";
import { CardContent, Typography, LinearProgress, Grid, Stack, List, ListItem, ListItemText, IconButton, Divider, Button, Alert, Autocomplete, TextField, Box, CircularProgress } from "@mui/material";
import Div from "@jumbo/shared/Div";
import _ from 'lodash';
import moment from 'moment';
import FilePresentOutlinedIcon from '@mui/icons-material/FilePresentOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import UnfoldLessOutlinedIcon from '@mui/icons-material/UnfoldLessOutlined';

import StateBadge from '../../widgets/Shared/StateBadge/StateBadge';
import { stateInfo } from '../../widgets/Shared/StateBadge/stateInfo';
import CalendarMonthTwoToneIcon from '@mui/icons-material/CalendarMonthTwoTone';
import AccessTimeTwoToneIcon from '@mui/icons-material/AccessTimeTwoTone';
import PersonOutlineTwoToneIcon from '@mui/icons-material/PersonOutlineTwoTone';
import AssignmentIndTwoToneIcon from '@mui/icons-material/AssignmentIndTwoTone';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import StoreTwoToneIcon from '@mui/icons-material/StoreTwoTone';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import PhonelinkEraseOutlinedIcon from '@mui/icons-material/PhonelinkEraseOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import MoreTimeOutlinedIcon from '@mui/icons-material/MoreTimeOutlined';
import LockClockOutlinedIcon from '@mui/icons-material/LockClockOutlined';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import PlaceIcon from '@mui/icons-material/Place';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import ImagesDownloadBtn from '../../widgets/Images/ImagesDownloadBtn';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import centreServices from 'app/services/centre-services';
import tripServices from 'app/services/trip-services';
import deliveryServices from 'app/services/delivery-services';
import orderServices from 'app/services/order-services';
import userServices from 'app/services/user-services';
import { inheritedAddressSchedule, vehicleIcon } from 'app/utils/appHelpers';
import { Badge, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from 'notistack';
import { useCompanySettings } from 'app/providers/CompanySettingsProvider/CompanySettingsProvider';
import { getDropoffCarrierByKey } from 'app/utils/dropoffCarriers';
import { canCloseManually } from 'app/utils/distribuidor';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StyledDatePicker, PICKER_LOCALE_TEXT } from 'app/widgets/Shared/StyledDatePicker/StyledDatePicker';

const TRIP_LIST_INCLUDE =
    'driver,vehicle.company,vehicle.transport_unit1,vehicle.transport_unit2,vehicle.centre.main_centre_zone,' +
    'deliveries.receipt,deliveries.survey_answers.survey_question,deliveries.order,' +
    'deliveries.docs_images_attachments,deliveries.locatable.location,' +
    'deliveries.delivery_details.images_attachments,deliveries.delivery_details.order_detail,' +
    'deliveries.delivery_docs.shipping_doc.shipping_doc_kind';
// La fecha se puede mover mientras nadie salio a la ruta; el backend aplica la misma regla.
const SHIP_DATE_EDITABLE_STATES = ['planned', 'pending'];
const TRIP_MANAGEMENT_PROFILE_NAMES = ['customer_admin', 'planner', 'supervisor'];
const WORKDAY_OVERRUN_PROFILE_NAMES = ['customer_admin', 'supervisor'];

// "45 min" o "1 h 10 min": un exceso de varias horas leido en minutos no se dimensiona.
const formatOverMinutes = (minutes) => {
    const total = Math.max(0, Math.round(minutes || 0));
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h === 0) return `${m} min`;
    return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

// Aviso de entregas fuera de jornada.
//
// La tarjeta del listado se quedo solo con esto. Distancia, costo y la comparacion
// de peajes se mudaron al instructivo: ahi es donde se decide, y en el listado
// llenaban la tarjeta de numeros que nadie estaba mirando.
//
// Esto se queda porque no es una metrica sino una alerta: hay entregas que segun
// el plan no alcanzan a hacerse.
const TripPlanningSummary = ({ deliveries }) => {
    const fueraDeJornada = (deliveries || []).filter((d) => d.fits_workday === false).length;
    if (fueraDeJornada === 0) return null;

    return (
        <React.Fragment>
            {fueraDeJornada > 0 && (
                <Div sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                    <Tooltip title="Según el plan, estas entregas quedan fuera del horario de entrega configurado">
                        <WarningAmberRoundedIcon fontSize="small" color="warning" />
                    </Tooltip>
                    <Typography variant="body2" color="warning.main" ml={1}>
                        {fueraDeJornada} {fueraDeJornada === 1 ? 'entrega fuera de jornada' : 'entregas fuera de jornada'}
                    </Typography>
                </Div>
            )}
        </React.Fragment>
    );
};

const TripListCard = ({ trip, labelSummary, onTripFocus, viewMoreActive, onViewMore, onDeliveryFocus, onDriverEdit, onScheduledTimeEdit, onDocsImagesView, onTripChange, onDeliveryChange }) => {
  const navigate = useNavigate();
  const { authUser } = useJumboAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { companySettings } = useCompanySettings();
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [preparers, setPreparers] = useState([]);
  const [editingDriver, setEditingDriver] = useState(false);
  const [tripState, setTripState] = useState(trip.state);
  const [stateName, setStateName] = useState(trip.state_name);
  const [editingVehicle, setEditingVehicle] = useState(false);
  const [editingPreparer, setEditingPreparer] = useState(false);
  const [selDriver, setSelDriver] = useState(trip.driver);
  const [selVehicle, setSelVehicle] = useState(trip.vehicle);
  const [selPreparer, setSelPreparer] = useState(null);
  const [isFieldEditing, setIsFieldEditing] = useState(false);
  const [observations, setObservations] = useState({});
  const [cancellingDeliveryId, setCancellingDeliveryId] = useState(null);
  const [confirmCancelDelivery, setConfirmCancelDelivery] = useState(null);
  const [savingPreparer, setSavingPreparer] = useState(false);
  const [editingShipDate, setEditingShipDate] = useState(false);
  const [savingShipDate, setSavingShipDate] = useState(false);

  let inputDriverRef;
  let inputVehicleRef;
  const tripDeliveryPreparerIds = _.map(trip.deliveries || [], (delivery) => delivery.preparer_user_id || null);
  const tripPreparers = _.chain(trip.deliveries || [])
      .map((delivery) => {
          if (!delivery.preparer_user_id || !delivery.preparer) return null;

          return {
              id: delivery.preparer_user_id,
              full_name: delivery.preparer,
          };
      })
      .compact()
      .uniqBy('id')
      .value();
  const tripHasAssignedPreparer = tripPreparers.length > 0;
  const allTripDeliveriesShareSamePreparer = tripDeliveryPreparerIds.length > 0
      && tripDeliveryPreparerIds.every((preparerId) => preparerId && preparerId === tripDeliveryPreparerIds[0]);
  const currentTripPreparer = allTripDeliveriesShareSamePreparer ? tripPreparers[0] : null;
  const tripPreparerLabel = !tripHasAssignedPreparer ? 'Sin asignar' : currentTripPreparer?.full_name || 'Asignacion parcial';
  const currentTripPreparerId = currentTripPreparer?.id || null;
  const currentTripPreparerName = currentTripPreparer?.full_name || null;
  const isTripFieldEnabled = (field) => companySettings?.[field] !== false;
  const showDriverField = isTripFieldEnabled('plan_require_driver');
  // Retiro en tienda: el cliente va a buscar el pedido a la bodega. El viaje se
  // planifica como cualquier otro —con chofer y vehiculo, porque la app filtra los
  // viajes por chofer y sin el nadie lo puede preparar— y lo unico que no tiene es
  // ruta: no hay recorrido que ordenar. Se rotula para que se entienda por que la
  // tarjeta no muestra km, duracion ni costo.
  const isPickupOnly = trip.pickup_only === true;
  const showVehicleField = isTripFieldEnabled('plan_require_vehicle');
  const showTransporterField = isTripFieldEnabled('plan_require_transportation_company');
  const showTripAssigneeField = showDriverField || showTransporterField;
  const showCombinedTripAssigneeField = showDriverField && showTransporterField;
  const showDropoffCarrierField = isTripFieldEnabled('plan_require_dropoff_carrier');
  const tripAssigneeLabel = showCombinedTripAssigneeField
      ? 'Conductor / Transportista'
      : showTransporterField
          ? 'Transportista'
          : 'Conductor';
  const tripAssigneeTooltip = `${tripAssigneeLabel} asignado al viaje`;
  const tripAssigneeOptions = showCombinedTripAssigneeField
      ? _.uniqBy([...drivers, ...transporters], 'id')
      : showTransporterField
          ? transporters
          : drivers;
  const tripAssigneeName = [trip.driver?.first_name, trip.driver?.last_name].filter(Boolean).join(' ') || 'Sin asignar';
  const currentCompanyId = authUser?.current_company?.id;
  const profileNames = Array.isArray(authUser?.profiles)
      ? authUser.profiles.map((profile) => profile?.name).filter(Boolean)
      : [];
  const currentCompanyProfileNames = Array.isArray(authUser?.profiles)
      ? authUser.profiles
          .filter((profile) => profile?.company_id === currentCompanyId)
          .map((profile) => profile?.name)
          .filter(Boolean)
      : [];
  const canManageTrip = profileNames.includes('superadmin') ||
      currentCompanyProfileNames.some((profileName) => TRIP_MANAGEMENT_PROFILE_NAMES.includes(profileName));
  // El aviso de jornada excedida es para quien puede reaccionar: admin y supervisor.
  const canSeeWorkdayOverrun = profileNames.includes('superadmin') ||
      currentCompanyProfileNames.some((profileName) => WORKDAY_OVERRUN_PROFILE_NAMES.includes(profileName));
  // Plan Distribuidor: sin app de conductor, el viaje se cierra a mano desde aca.
  const canCloseTrip = canCloseManually(authUser) && tripState !== 'finished';
  const [confirmCloseTrip, setConfirmCloseTrip] = useState(false);
  const [closingTrip, setClosingTrip] = useState(false);
  const openDeliveriesCount = (trip.deliveries || []).filter((d) => !['finished', 'cancelled'].includes(d.state)).length;

  // Se relee con el mismo include del listado: las paradas y sus pedidos cambiaron.
  const reloadTrip = () => tripServices.getTripDetails(trip.id, `?include=${encodeURIComponent(TRIP_LIST_INCLUDE)}`)
      .then((data) => {
          setTripState(data.state);
          setStateName(data.state_name);
          // El detalle trae el conductor sin sus telefonos: se conservan los del listado.
          onTripChange({ ...data, driver: trip.driver, vehicle: trip.vehicle });
          return data;
      });

  const closeTrip = () => {
      setClosingTrip(true);
      tripServices.manualClose(trip.id)
          .then((closed) => {
              enqueueSnackbar(`Viaje ${trip.code} cerrado`, { variant: 'success' });
              // El viaje ya quedo cerrado: si falla la relectura no es un error del cierre, se
              // muestra con lo que devolvio el cierre.
              return reloadTrip().catch(() => {
                  setTripState(closed?.state || 'finished');
                  setStateName(closed?.state_name || 'Finalizado');
              });
          })
          .catch((error) => {
              enqueueSnackbar(error?.response?.data?.error || error?.error || 'No fue posible cerrar el viaje', { variant: 'error' });
          })
          .finally(() => {
              setClosingTrip(false);
              setConfirmCloseTrip(false);
          });
  };
  // Plan Distribuidor: finalizar una sola parada. Va por el estado manual del pedido, que deja
  // su parada entregada y, si era la ultima abierta, cierra el viaje.
  const [confirmFinishDelivery, setConfirmFinishDelivery] = useState(null);
  const [finishingDeliveryId, setFinishingDeliveryId] = useState(null);
  const finishDelivery = (delivery) => {
      setFinishingDeliveryId(delivery.id);
      orderServices.updateManualState(delivery.order.id, 'finished')
          // Como en closeTrip: el pedido ya quedo finalizado aunque falle la relectura.
          .then(() => reloadTrip().catch(() => null))
          .then((data) => {
              enqueueSnackbar(data?.state === 'finished'
                  ? `Pedido ${delivery.order.code} finalizado. Era la última parada: el viaje ${trip.code} quedó cerrado`
                  : `Pedido ${delivery.order.code} finalizado`, { variant: 'success' });
          })
          .catch((error) => {
              enqueueSnackbar(error?.response?.data?.error || error?.error || 'No fue posible finalizar el pedido', { variant: 'error' });
          })
          .finally(() => {
              setFinishingDeliveryId(null);
              setConfirmFinishDelivery(null);
          });
  };
  const driverPhones = _.compact([trip?.driver?.phone_primary, trip?.driver?.phone_secondary]);
  const hasDriverPhone = driverPhones.length > 0;
  const driverContactTooltip = hasDriverPhone
      ? (canManageTrip ? 'Editar contacto del conductor' : 'Ver contacto del conductor')
      : 'El conductor no tiene telefono registrado';
  useEffect(() => {
      if (authUser) {
        //   console.log(authUser.current_company);
      } else {
          console.log('authUser is not defined yet.');
      }
  }, [authUser]);

  useEffect(() => {
      moment.locale('es');
  }, []);

  useEffect(() => {
    if (trip.state === 'pending' && authUser?.current_company?.id === 80) {
        setTripState('in_progress');
        setStateName('En progreso');
    }
    }, [trip, authUser]);

  useEffect(() => {
      setSelDriver(trip.driver || null);
      setSelVehicle(trip.vehicle || null);
  }, [trip.driver, trip.vehicle]);

  useEffect(() => {
      if (editingVehicle) {
          inputVehicleRef.focus();
          if (vehicles.length <= 0) {
              updateOptions();
          }
      } else {
          if (selVehicle && selVehicle.id !== trip.vehicle?.id) {
              updateTrip(trip.id, { vehicle_id: selVehicle.id });
          }
      }
  }, [editingVehicle]);

  useEffect(() => {
      if (editingDriver) {
          inputDriverRef.focus();
          if (drivers.length <= 0) {
              updateOptions();
          }
      } else {
          if (selDriver && selDriver.id !== trip.driver?.id) {
              updateTrip(trip.id, { driver_id: selDriver.id });
          }
      }
  }, [editingDriver]);

  useEffect(() => {
      // Initialize observations with existing values
      const initialObservations = {};
      trip.deliveries.forEach((d) => {
          initialObservations[d.id] = d.receipt?.observations || '';
      });
      setObservations(initialObservations);
  }, [trip.deliveries]);

  useEffect(() => {
      if (currentTripPreparerId && currentTripPreparerName) {
          setSelPreparer({
              id: currentTripPreparerId,
              full_name: currentTripPreparerName,
          });
      } else {
          setSelPreparer(null);
      }
  }, [currentTripPreparerId, currentTripPreparerName]);

  const toggleEditing = (id) => {
    setIsFieldEditing((prev) => !prev);
  };

  const updateOptions = () => {
      if (trip.centre?.id) {
          centreServices.getCentreOptions(trip.centre?.id)
              .then(data => {
                  setDrivers(data?.data?.drivers || []);
                  setTransporters(data?.data?.transporters || []);
                  setVehicles(data?.data?.vehicles || []);
              });
      }
  };

  const loadPreparers = () => {
      userServices.getPreparerUsers()
          .then((data) => {
              setPreparers(data);
          })
          .catch(() => {
              enqueueSnackbar('No fue posible cargar los preparadores', {
                  variant: 'error',
              });
          });
  };

  const updateTrip = (trip_id, params) => {
      tripServices.updateTrip(trip_id, "?include=driver,vehicle.company,vehicle.transport_unit1,vehicle.transport_unit2", params)
          .then(data => {
              onTripChange(data);
          });
  };

  const updateTripShipDate = (newDate) => {
      setEditingShipDate(false);
      if (!newDate || moment(newDate).isSame(trip.ship_date, 'day')) return;

      setSavingShipDate(true);
      tripServices.updateTrip(
          trip.id,
          `?include=${encodeURIComponent(TRIP_LIST_INCLUDE)}`,
          { ship_date: moment(newDate).startOf('day').format() }
      )
          .then((data) => {
              onTripChange(data);
              enqueueSnackbar('Fecha del viaje actualizada', { variant: 'success' });
          })
          .catch((error) => {
              enqueueSnackbar(
                  error?.response?.data?.error || 'No fue posible cambiar la fecha del viaje',
                  { variant: 'error' }
              );
          })
          .finally(() => {
              setSavingShipDate(false);
          });
  };

  const assignTripPreparer = (tripId, preparerId) => {
      setSavingPreparer(true);

      tripServices.assignPreparer(
          tripId,
          `?include=${encodeURIComponent(TRIP_LIST_INCLUDE)}`,
          { preparer_id: preparerId }
      )
          .then((data) => {
              onTripChange(data);
              enqueueSnackbar('Preparador asignado correctamente al viaje', {
                  variant: 'success',
              });
          })
          .catch((error) => {
              setSelPreparer(currentTripPreparer);
              enqueueSnackbar(
                  error?.response?.data?.error || 'No fue posible asignar el preparador al viaje',
                  { variant: 'error' }
              );
          })
          .finally(() => {
              setSavingPreparer(false);
          });
  };

  const openPreparerEditor = () => {
      if (preparers.length <= 0) {
          loadPreparers();
      }

      setEditingPreparer(true);
  };

  const handlePreparerSelection = (preparer) => {
      setSelPreparer(preparer);
      setEditingPreparer(false);

      if (!preparer) return;

      const shouldAssignPreparer = currentTripPreparer?.id !== preparer.id || !allTripDeliveriesShareSamePreparer;

      if (shouldAssignPreparer) {
          assignTripPreparer(trip.id, preparer.id);
      }
  };

    const handleOcChange = (id, value) => {
        setObservations((prev) => ({
        ...prev,
        [id]: value,
        }));
    };

  const saveOcChange = (deliveryId, receiptId) => {
      deliveryServices.updateReceipt(deliveryId, { observations: observations[deliveryId] })
          .then(updatedDelivery => {

              const updatedDeliveries = trip.deliveries.map(d =>
                  d.id === updatedDelivery.id ? { ...d, receipt: updatedDelivery.receipt } : d
              );

              const updatedTrip = { ...trip, deliveries: updatedDeliveries };
              onTripChange(updatedTrip);
              onDeliveryChange(updatedDelivery);  // This is to update the delivery in the parent component
          })
          .catch(error => {
              console.error('Failed to update delivery:', error);
          });
  };

  const orderStateName = (state, fallbackName) => {
      if (state === 'pending') return 'Pendiente';
      if (state === 'in_progress') return 'En progreso';
      if (state === 'invoiced') return 'Documentado';
      if (state === 'finished') return 'Finalizado';
      if (state === 'deleted') return 'Eliminado';
      if (state === 'cancelled') return 'Cancelado';
      return fallbackName || state;
  };

  const cancelOrderInTrip = async (delivery) => {
      if (!trip?.id) return;

      setCancellingDeliveryId(delivery.id);
      try {
          let response;
          if (delivery.order?.id) {
              response = await orderServices.cancelForTrip(delivery.order.id, trip.id);
          } else {
              response = await deliveryServices.cancelForTrip(delivery.id, trip.id);
          }

          const updatedDeliveries = trip.deliveries.map((currDelivery) => {
              if (currDelivery.id !== delivery.id) return currDelivery;

              const updated = { ...currDelivery, state: 'cancelled', state_name: 'Cancelado' };
              if (currDelivery.order) {
                  const nextOrderState = response?.order_state || currDelivery.order?.state;
                  updated.order = {
                      ...currDelivery.order,
                      state: nextOrderState,
                      state_name: orderStateName(nextOrderState, currDelivery.order?.state_name),
                  };
              }
              return updated;
          });

          onTripChange({ ...trip, deliveries: updatedDeliveries });
      } catch (error) {
          console.error('Failed to cancel delivery in trip:', error);
      } finally {
          setCancellingDeliveryId(null);
      }
  };

  const OnReceptionFocus = (d) => {
  }
  const preOnDeliveryFocus = (d) => {
    // Manually update the observations for the delivery object
    const updatedObservations = observations[d.id] ?? d.receipt?.observations ?? '';
    const updatedReceipt = { ...d.receipt, observations: updatedObservations };
    const updatedDelivery = { ...d, receipt: updatedReceipt };
  
    onDeliveryFocus(updatedDelivery);
  };
  

  const waterColor = trip.metadata?.water_ok ? 'text.secondary' : 'error'; // Assuming 'error' maps to red or tertiary color in your theme
  const tripImagesCount = _.sumBy(trip.deliveries, (d) => (d.docs_images_count + d.images_count));

  const renderCarrierBadge = (src, alt, title, style = {}) => (
      <Tooltip title={title}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <img
                  src={src}
                  alt={alt}
                  style={style}
              />
          </Box>
      </Tooltip>
  );

  const getTripAssigneeOptionLabel = (option) => {
      if (!option) return '';
      if (_.isString(option)) return option;

      const fullName = [option.first_name, option.last_name].filter(Boolean).join(' ');
      return `${option.code ? option.code : "S/C"} - ${fullName}`;
  };

  const renderTripAssigneeIcon = () => {
      if (showCombinedTripAssigneeField) {
          return <ManageAccountsOutlinedIcon fontSize="small" color={"primary"} />;
      }

      if (showTransporterField) {
          return <LocalShippingOutlinedIcon fontSize="small" color={"primary"} />;
      }

      return <PersonOutlineTwoToneIcon fontSize="small" color={"primary"} />;
  };

  const renderDeliveries = () => {
    
      if (trip.deliveries) {
          return (
              <List
                  dense={true}
                  style={{ maxHeight: viewMoreActive ? '500px' : '160px', overflow: 'auto', transitionDuration: '0.3s' }}
                  sx={{ padding: 0 }}
              >
                  {_.map(trip.deliveries, (d) => {
                    const formattedTin = formatReceiptTin(d.receipt?.tin);
                    const isSuperadmin = _.some(authUser?.profiles, { name: 'superadmin' });
                    const isTripInProgress = trip.state === 'in_progress';
                    const isTripFinished = trip.state === 'finished';
                    const showPlanningEstimate = isTripInProgress || isTripFinished;
                    const isDeliveryFinished = d.state === 'finished';
                    const canCancelOrderInCard = isSuperadmin
                        && !_.includes(['cancelled', 'finished'], d.state)
                        && (!d.order?.id || _.includes(['pending', 'in_progress', 'invoiced'], d.order?.state));
                    const planningEstimateText = d.planned_start && d.planned_end
                        ? `P: ${moment(d.planned_end).format('DD MMM HH:mm')} (${printTimeDiff(
                            d.planned_start,
                            d.planned_end
                        )})${isDeliveryFinished && d.final_end ? ` - R: ${moment(d.final_end).format('DD MMM HH:mm')}` : ''}`
                        : 'Sin planificación';
                    const timeWindow = renderTimeWindow(d);
                    const hasDropoffIcon = Boolean(
                        showDropoffCarrierField
                        && (d.dropoff ?? d.order?.dropoff)
                        && (d.dropoff_carrier || d.order?.dropoff_carrier)
                    );
                      return (
                          <ListItem
                              key={`delivery-${d.id}`} // Add a unique key
                              alignItems="flex-start"
                              id={d.id}
                          >
                              <ListItemText
                                  id={d.id}
                                  primary={
                                      <Stack direction="row" alignItems="center" spacing={1} sx={{ maxWidth: 400 }}>
                                          <Tooltip title={d.locatable?.has_location ? 'Entrega con ubicacion georreferenciada' : 'Entrega sin ubicacion georreferenciada'}>
                                              <PlaceIcon color={d.locatable?.has_location ? 'success' : 'error'} sx={{ fontSize: 14 }} />
                                          </Tooltip>
                                          <Typography  variant="body2" sx={{ display: 'inline', fontWeight: 350 }} color="#2EB5C9" >
                                          {d.code} {d.locatable?.name || ''}
                                          </Typography>
                                          {d.reception_msg ? (
                                              <Tooltip title={d.reception_msg.msg || 'Mensaje de recepcion'}>
                                                  <WarningAmberRoundedIcon color={d.reception_msg.kind} />
                                              </Tooltip>
                                          ) : null}
                                      </Stack>
                                  }
                                  secondary={
                                      <React.Fragment>
                                          {authUser?.current_company?.id !== 80 && (
                                              <>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={1}>
                                                    <Typography variant="body2" sx={{ textAlign: 'left' }} color="text.secondary">
                                                        Ped: {d.order?.code}
                                                    </Typography>
                                                    {d.order?.plugin_source === 'mercado-libre' && (
                                                        <>
                                                            {renderCarrierBadge(
                                                                '/images/icons/mercado-libre-color.svg',
                                                                'Mercado Libre',
                                                                'Pedido de Mercado Libre',
                                                                { height: 14, marginLeft: 4 }
                                                            )}
                                                            {(() => {
                                                                const sm = String(d.order?.shipping_method || '').toLowerCase().trim();
                                                                const lt = String(d.order?.logistic_type || '').toLowerCase().trim();

                                                                if (sm === 'fulfillment') {
                                                                    return renderCarrierBadge(
                                                                        '/images/icons/meli-full.svg',
                                                                        'Full',
                                                                        'Logistica Mercado Libre Full',
                                                                        { height: 19, marginLeft: 4, paddingBottom: 0 }
                                                                    );
                                                                }

                                                                if (sm === 'me2' && lt === 'self_service') {
                                                                    return renderCarrierBadge(
                                                                        '/images/icons/meli-flex.svg',
                                                                        'Flex',
                                                                        'Logistica Mercado Libre Flex',
                                                                        { height: 20, marginLeft: 4 }
                                                                    );
                                                                }

                                                                if (lt === 'xd_drop_off') {
                                                                    return renderCarrierBadge(
                                                                        '/images/icons/meli-dropoff.svg',
                                                                        'Drop Off',
                                                                        'Logistica Mercado Libre Drop Off',
                                                                        { height: 17, marginLeft: 4 }
                                                                    );
                                                                }

                                                                if (lt === 'pickup') {
                                                                    return renderCarrierBadge(
                                                                        '/images/icons/meli-pickup.svg',
                                                                        'Pickup',
                                                                        'Logistica Mercado Libre Pickup',
                                                                        { height: 15, marginLeft: 4 }
                                                                    );
                                                                }

                                                                return null;
                                                            })()}
                                                        </>
                                                    )}

                                                    {(() => {
                                                        const dropoffEnabled = Boolean(d.dropoff ?? d.order?.dropoff);
                                                        const dropoffCarrier = d.dropoff_carrier || d.order?.dropoff_carrier;
                                                        if (!showDropoffCarrierField || !dropoffEnabled || !dropoffCarrier) return null;

                                                        return (
                                                            <Tooltip title="Punto de retiro (Dropoff)">
                                                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                                    {(() => {
                                                                        const dropoffCarrierConfig = getDropoffCarrierByKey(
                                                                            dropoffCarrier,
                                                                            companySettings?.available_dropoff_carriers
                                                                        );
                                                                        const carrierIcon = dropoffCarrierConfig?.iconPath;

                                                                        if (!carrierIcon) {
                                                                            return (
                                                                                <Typography variant="caption" sx={{ ml: 0.75 }}>
                                                                                    {dropoffCarrierConfig?.label || dropoffCarrier}
                                                                                </Typography>
                                                                            );
                                                                        }

                                                                        return (
                                                                    <img
                                                                        src={carrierIcon}
                                                                        alt="Dropoff"
                                                                        style={{
                                                                            height: 16,
                                                                            marginLeft: 6,
                                                                            borderRadius: 4,
                                                                            border: '1px solid #eee'
                                                                        }}
                                                                    />
                                                                        );
                                                                    })()}
                                                                </Box>
                                                            </Tooltip>
                                                        );
                                                    })()}


                                                    {_.map(d.delivery_docs, (dd) => (
                                                        <Typography
                                                            key={`doc-${dd.id}`}
                                                            variant="body2"
                                                            sx={{ textAlign: 'left' }}
                                                            color="text.secondary"
                                                        >
                                                            {dd.shipping_doc?.shipping_doc_kind?.slug === 'delivery-receipt'
                                                                ? 'Guía: '
                                                                : 'Fact: '}
                                                            {dd.shipping_doc?.customer_code}
                                                        </Typography>
                                                    ))}
                                                </Stack>
                                                {!showPlanningEstimate && timeWindow && (
                                                    <Box component="span" sx={{ ml: hasDropoffIcon ? 0.75 : 0 }}>
                                                        {timeWindow}
                                                    </Box>
                                                )}
                                                </Stack>
                                                  {showPlanningEstimate && (
                                                      <Stack direction="row" alignItems="center" spacing={1}>
                                                          <Typography variant="body2" color="text.secondary">
                                                              {planningEstimateText}
                                                          </Typography>
                                                          {timeWindow}
                                                      </Stack>
                                                  )}
                                                  {d.order?.comment && (
                                                      <Alert
                                                          severity="info"
                                                          icon={<CommentOutlinedIcon fontSize="small" />}
                                                          sx={{
                                                              '&.MuiAlert-root': {
                                                                  display: 'inline-flex',
                                                                  px: 0.5,
                                                                  py: 0,
                                                                  fontSize: 11,
                                                              },
                                                          }}
                                                      >
                                                          {d.order.comment}
                                                      </Alert>
                                                  )}
                                              </>
                                          )}
                                          {authUser?.current_company?.id === 80 && (
                                              <>
                                                  <Typography variant="body2" sx={{ ml: 2.675, textAlign: 'left', mt: -0.5 }} color="text.secondary">
                                                      {d.locatable?.alias}
                                                  </Typography>

                                                  <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography sx={{ ml: 1 }} variant="body2" color="text.secondary">
                                                        {d.receipt?.completed_at ? (
                                                            <>
                                                                Entregado:&nbsp;&nbsp;&nbsp;{moment(d.receipt.completed_at).format('DD MMM HH:mm')}
                                                            </>
                                                        ) : (
                                                            'Sin planificación'
                                                        )}
                                                    </Typography>
                                                    {timeWindow}
                                                </Stack>
                                                  <Stack direction="row" alignItems="center" spacing={1}>
                                                      <Tooltip title="Producto y volumen entregado">
                                                          <LocalGasStationIcon fontSize="small" sx={{ ml: 0.475, color: '#2EB5C9' }} />
                                                      </Tooltip>
                                                      <Typography variant="body2" sx={{ ml: 1 }} color="text.secondary">
                                                          {d.delivery_details?.[0]?.product ? `${d.delivery_details[0].product} ` : ''}{d.delivery_details?.[0]?.delivered_qty?.toLocaleString()} L
                                                      </Typography>
                                                  </Stack>
                                                  <Stack direction="row" alignItems="center" spacing={1}>
                                                      <Tooltip title="Observaciones del producto entregado">
                                                          <WaterDropIcon fontSize="small" sx={{ ml: 0.375, color: '#2EB5C9' }} />
                                                      </Tooltip>
                                                      <Typography variant="body2" sx={{ ml: 1 }} color="text.secondary">
                                                          {d.delivery_details?.[0]?.observations ? `${d.delivery_details[0].observations} ` : ''}
                                                      </Typography>
                                                  </Stack>
                                                  <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Tooltip title="Datos de recepcion del destinatario">
                                                            <PersonOutlineTwoToneIcon fontSize="small" sx={{ ml: 0.375, color: '#2EB5C9' }} />
                                                        </Tooltip>
                                                        <Typography variant="body2" sx={{ ml: 1 }} color="text.secondary">
                                                        {formattedTin ? `${formattedTin} ` : ''}{d.receipt?.name ? `${d.receipt.name}` : ''}
                                                        </Typography>
                                                  </Stack>
                                                  <Box key={`receipt-${d.id}`} sx={{ display: 'flex', alignItems: 'center', mt: -0.8 }}>
                                                      <Tooltip title="Observaciones de la recepcion">
                                                          <ReceiptLongOutlinedIcon
                                                              fontSize="small"
                                                              sx={{ ml: 0.265, color: '#2EB5C9', display: 'flex', alignItems: 'center' }} />
                                                      </Tooltip>
                                                      <TextField
                                                          variant="standard"
                                                          size="small"
                                                          value={observations[d.id] ?? d.receipt?.observations ?? ''}
                                                          onChange={(e) => handleOcChange(d.id, e.target.value)}
                                                          InputProps={{
                                                              disableUnderline: true,
                                                          }}
                                                          sx={{ width: '100px', ml: 1.375, mt: 0.5 }}
                                                          disabled={!isFieldEditing} />
                                                      <Button
                                                          onClick={() => {
                                                              if (isFieldEditing) {
                                                                  saveOcChange(d.id, d.receipt?.id);
                                                              }
                                                              toggleEditing(d.id);
                                                          }}
                                                          sx={{ ml: 1, fontSize: '10px' }}
                                                      >
                                                          {isFieldEditing ? 'Guardar' : 'Editar'}
                                                      </Button>
                                                  </Box>
                                              </>
                                          )}
                                      </React.Fragment>
                                  }
                              />
                              <Stack
                                  direction="row"
                                  justifyContent="flex-end"
                                  alignItems="center"
                                  spacing={0.5}>
                                  {canCloseTrip && d.order?.id && !_.includes(['cancelled', 'finished'], d.state) && (
                                      <Tooltip title="Finalizar este pedido">
                                          <span>
                                              <IconButton
                                                  edge="end"
                                                  size="small"
                                                  disabled={finishingDeliveryId === d.id}
                                                  onClick={() => setConfirmFinishDelivery(d)}
                                              >
                                                  {finishingDeliveryId === d.id ? (
                                                      <CircularProgress size={16} />
                                                  ) : (
                                                      <TaskAltOutlinedIcon fontSize="small" color="success" />
                                                  )}
                                              </IconButton>
                                          </span>
                                      </Tooltip>
                                  )}
                                  {canCancelOrderInCard && (
                                      <Tooltip title="Cancelar pedido en este viaje">
                                          <IconButton
                                              edge="end"
                                              size="small"
                                              sx={{ "&:hover": { color: theme => theme.palette.error.main } }}
                                              disabled={cancellingDeliveryId === d.id}
                                              onClick={() => setConfirmCancelDelivery(d)}
                                          >
                                              {cancellingDeliveryId === d.id ? (
                                                  <CircularProgress size={16} />
                                              ) : (
                                                  <CancelOutlinedIcon fontSize="small" color="error" />
                                              )}
                                          </IconButton>
                                      </Tooltip>
                                  )}
                                  <StateBadge state={d.state} stateName={d.state_name} withIcon={false} />
                                  <Stack direction="row" spacing={0}>
                                      <Tooltip title={d.docs_images_count > 0 ? 'Ver documentos tributarios' : 'Sin documentos tributarios disponibles'}>
                                          <span>
                                              <IconButton edge="end" aria-label="Documentos tributarios" onClick={() => onDocsImagesView(d)} disabled={d.docs_images_count <= 0}>
                                                  <FilePresentOutlinedIcon fontSize="small" color={d.docs_images_count > 0 ? 'success' : 'disabled'} />
                                              </IconButton>
                                          </span>
                                      </Tooltip>
                                      <Tooltip title={d.receipt ? 'Ver detalle de la entrega y recepcion' : 'La entrega aun no tiene recepcion registrada'}>
                                          <span>
                                              <IconButton edge="end" aria-label="Detalle de entregas" onClick={() => preOnDeliveryFocus(d)}>
                                              <FactCheckOutlinedIcon fontSize="small" color={d.receipt ? 'success' : 'disabled'} />
                                              </IconButton>
                                          </span>
                                      </Tooltip>
                                  </Stack>
                              </Stack>
                          </ListItem>
                      );
                  })}
              </List>
          );
      }
      return null;
  };

  const printTimeDiff = (_start, _end) => {
      _start = moment(_start);
      _end = moment(_end);
      const _diffMins = _start > _end ? _start.diff(_end, 'minutes') : _end.diff(_start, 'minutes');
      const hhs = _.floor(_diffMins / 60);
      const mms = _diffMins % 60;
      return `${_start > _end ? '-' : ''}${hhs > 0 ? `${hhs}h y ` : ''}${mms}min`;
  };

  const renderTimeWindow = (del) => {
      // El horario preferente solo aplica a entregas activas: planificadas,
      // pendientes o en progreso. Las finalizadas o canceladas no muestran nada.
      if (!_.includes(['planned', 'pending', 'in_progress'], del.state)) {
          return null;
      }

      if (del.preferred_arrival_start || del.preferred_arrival_end) {
          return (
              <Tooltip title="Horario preferente configurado para esta entrega">
                  <span>
                      <Button variant="outlined" size="small" color={del.meets_preferred_time_window === true ? 'success' : 'error'} sx={{ p: 0 }} onClick={() => onScheduledTimeEdit(del)} >
                          <LockClockOutlinedIcon fontSize='small' />{del.preferred_arrival_start}-{del.preferred_arrival_end}
                      </Button>
                  </span>
              </Tooltip>
          );
      }

      // Sin horario propio, se muestra el configurado en la dirección del
      // cliente (Clientes > horario preferente por dirección). Se distingue del
      // propio con color 'info' porque aún no está fijado en esta entrega.
      const inherited = inheritedAddressSchedule(del);
      if (inherited) {
          return (
              <Tooltip title="Horario preferente de la dirección del cliente. Click para fijar uno propio de esta entrega.">
                  <span>
                      <Button variant="outlined" size="small" color="info" sx={{ p: 0 }} onClick={() => onScheduledTimeEdit(del)}>
                          <LockClockOutlinedIcon fontSize='small' />{inherited.start || ''}-{inherited.end || ''}
                      </Button>
                  </span>
              </Tooltip>
          );
      }

      return (
          <Tooltip title="Definir horario preferente de entrega">
              <Button size="small" sx={{ p: 0 }} onClick={() => onScheduledTimeEdit(del)}>
                  <MoreTimeOutlinedIcon fontSize='small' />
              </Button>
          </Tooltip>
      );
  };

  return (
      <>
      <Card key={trip.id} sx={{ mb: 2 }}>
          <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                  <Grid item xs={12} md={3} py={0}>
                      <Typography variant="h4" color="primary">Nº {trip.code}
                      {authUser?.current_company?.id === 80 && (
                            <Tooltip title="Ver detalle de recepcion del viaje">
                                <IconButton sx={{ pb: 1.4 }} edge="end" aria-label="Detalle de recepción" onClick={() => OnReceptionFocus()}>
                                    <FactCheckOutlinedIcon fontSize="small" color={trip.code ? 'success' : 'disabled'} />
                                </IconButton>
                            </Tooltip>
                        )}
                        </Typography>
                      <Div sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                          <Tooltip title="Fecha de despacho del viaje">
                              <CalendarMonthTwoToneIcon fontSize="small" color={"primary"} />
                          </Tooltip>
                          {editingShipDate ? (
                              <Box ml={1}>
                                  <LocalizationProvider dateAdapter={AdapterDateFns} localeText={PICKER_LOCALE_TEXT}>
                                      <StyledDatePicker
                                          open
                                          format="dd/MM/yyyy"
                                          value={trip.ship_date ? new Date(trip.ship_date) : new Date()}
                                          onAccept={updateTripShipDate}
                                          onClose={() => setEditingShipDate(false)}
                                          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                                      />
                                  </LocalizationProvider>
                              </Box>
                          ) : (
                              <>
                                  <Typography variant="body2" color={"text.secondary"} ml={1}>{moment(trip.ship_date).format('DD MMM')}</Typography>
                                  {SHIP_DATE_EDITABLE_STATES.includes(trip.state) && (
                                      <Tooltip title="Cambiar la fecha del viaje">
                                          <span>
                                              <IconButton edge="end" sx={{ p: 0, ml: 1 }} onClick={() => setEditingShipDate(true)} disabled={savingShipDate}>
                                                  {savingShipDate
                                                      ? <CircularProgress size={14} />
                                                      : <EditOutlinedIcon fontSize="small" color={"disabled"} />}
                                              </IconButton>
                                          </span>
                                      </Tooltip>
                                  )}
                              </>
                          )}
                      </Div>
                      <Div sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                          <Tooltip title="Centro asociado al viaje">
                              <StoreTwoToneIcon fontSize="small" color={"primary"} />
                          </Tooltip>
                          <Typography variant="body2" color="text.secondary" ml={1}>
                            {trip.centre?.code ? `${trip.centre?.code} - ${trip.centre?.name}` : trip.centre?.name}
                          </Typography>
                      </Div>
                      <TripPlanningSummary deliveries={trip.deliveries} />
                      {isPickupOnly && (
                          <Div sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                              <Tooltip title="El cliente retira el pedido en la bodega: se prepara igual que cualquier viaje, pero no se calcula ruta">
                                  <StorefrontOutlinedIcon fontSize="small" color="primary" />
                              </Tooltip>
                              <Typography variant="body2" color="text.secondary" ml={1}>
                                  Retiro en tienda
                              </Typography>
                          </Div>
                      )}
                      {showVehicleField && (
                          <Div sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                              <Tooltip title="Vehiculo asignado al viaje">
                                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                      {vehicleIcon(trip.vehicle)}
                                  </Box>
                              </Tooltip>
                              {editingVehicle ?
                                  <Autocomplete
                                      id="centre-select"
                                      size="small"
                                      freeSolo
                                      blurOnSelect
                                      fullWidth
                                      openOnFocus
                                      sx={{ pl: 1, maxWidth: 200 }}
                                      options={vehicles}
                                      getOptionLabel={(option) => `${option.code ? option.code : "S/C"} - ${option.transport_unit1?.ppu}${option.transport_unit2 ? `/${option.transport_unit2.ppu}` : ''}`}
                                      value={trip.vehicle}
                                      onChange={(e, v) => { setSelVehicle(v); }}
                                      onBlur={(e, v) => { setEditingVehicle(false); }}
                                      renderInput={(params) => <TextField {...params} variant="standard" label="Vehículo" sx={{ background: 'white' }} inputRef={input => { inputVehicleRef = input; }} />}
                                  />
                                  :
                                  <React.Fragment>
                                      <Typography variant="body2" color={"text.secondary"} ml={1}>{trip.vehicle?.transport_unit1?.ppu} {trip.vehicle?.transport_unit2 && `/ ${trip.vehicle?.transport_unit2?.ppu}`}</Typography>
                                      {_.includes(['planned', 'pending'], trip.state) &&
                                          <Tooltip title="Editar vehiculo del viaje">
                                              <IconButton edge="end" sx={{ p: 0 }} onClick={() => { setEditingVehicle(true); }}>
                                                  <EditOutlinedIcon fontSize="small" color={"disabled"} />
                                              </IconButton>
                                          </Tooltip>
                                      }
                                  </React.Fragment>
                              }
                          </Div>
                      )}
                      {/* Hora real en que se inicio la ruta (Resume::TripSerializer#started_at). */}
                      {trip.started_at && (
                          <Div sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                              <Tooltip title="Hora de inicio de la ruta">
                                  <AccessTimeTwoToneIcon fontSize="small" color={"primary"} />
                              </Tooltip>
                              <Typography variant="body2" color="text.secondary" ml={1}>
                                  Inicio {moment(trip.started_at).format('HH:mm')}
                              </Typography>
                          </Div>
                      )}
                      {showTripAssigneeField && (
                          <Div sx={{ display: 'flex', alignItems: 'start', mb: 0 }}>
                              <Tooltip title={tripAssigneeTooltip}>
                                  {renderTripAssigneeIcon()}
                              </Tooltip>
                              {editingDriver ?
                                  <Autocomplete
                                      id="trip-assignee-select"
                                      size="small"
                                      freeSolo
                                      blurOnSelect
                                      fullWidth
                                      openOnFocus
                                      sx={{ pl: 1, maxWidth: 200 }}
                                      options={tripAssigneeOptions}
                                      getOptionLabel={getTripAssigneeOptionLabel}
                                      isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                                      value={selDriver}
                                      onChange={(e, v) => { setSelDriver(v); }}
                                      onBlur={(e, v) => { setEditingDriver(false); }}
                                      renderInput={(params) => <TextField {...params} variant="standard" label={tripAssigneeLabel} sx={{ background: 'white' }} inputRef={input => { inputDriverRef = input; }} />}
                                  />
                                  :
                                  <React.Fragment>
                                      <Typography variant="body2" color={"text.secondary"} ml={1}>{tripAssigneeName}</Typography>
                                      {_.includes(['planned', 'pending'], trip.state) &&
                                          <Tooltip title={`Editar ${tripAssigneeLabel.toLowerCase()} del viaje`}>
                                              <IconButton edge="end" sx={{ p: 0 }} onClick={() => { setEditingDriver(true); }}>
                                                  <EditOutlinedIcon fontSize="small" color={"disabled"} />
                                              </IconButton>
                                          </Tooltip>
                                      }
                                  </React.Fragment>
                              }
                          </Div>
                      )}
                      <Div sx={{ display: 'flex', alignItems: 'start', mb: 0 }}>
                          <Tooltip title="Preparador asignado a todos los pedidos del viaje">
                              <AssignmentIndTwoToneIcon fontSize="small" color={"primary"} />
                          </Tooltip>
                          {editingPreparer ?
                              <Autocomplete
                                  id="trip-preparer-select"
                                  size="small"
                                  blurOnSelect
                                  fullWidth
                                  openOnFocus
                                  sx={{ pl: 1, maxWidth: 220 }}
                                  options={preparers}
                                  getOptionLabel={(option) => option?.full_name || ''}
                                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                                  value={selPreparer}
                                  onChange={(e, v) => { handlePreparerSelection(v); }}
                                  onBlur={() => { setEditingPreparer(false); }}
                                  renderInput={(params) => <TextField {...params} variant="standard" label="Preparador" sx={{ background: 'white' }} />}
                              />
                              :
                              <React.Fragment>
                                  <Typography variant="body2" color={"text.secondary"} ml={1}>{tripPreparerLabel}</Typography>
                                  {_.includes(['planned', 'pending'], trip.state) &&
                                      <Tooltip title={tripHasAssignedPreparer ? 'Editar preparador del viaje' : 'Asignar preparador al viaje'}>
                                          <span>
                                              <IconButton edge="end" sx={{ p: 0 }} onClick={openPreparerEditor} disabled={savingPreparer}>
                                                  {savingPreparer ? (
                                                      <CircularProgress size={16} />
                                                  ) : tripHasAssignedPreparer ? (
                                                      <EditOutlinedIcon fontSize="small" color={"disabled"} />
                                                  ) : (
                                                      <AddCircleOutlineOutlinedIcon fontSize="small" color="success" />
                                                  )}
                                              </IconButton>
                                          </span>
                                      </Tooltip>
                                  }
                              </React.Fragment>
                          }
                      </Div>
                      {authUser?.current_company?.id === 80 && (
                          <Div sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                              <Tooltip title="Resultado de la prueba de agua">
                                  <WaterDropIcon fontSize="small" color={"primary"} />
                              </Tooltip>
                              <Typography variant="body2" color={waterColor} ml={1}>{trip.metadata?.water_test}</Typography>
                          </Div>
                      )}
                      <Div sx={{ mt: 1 }}>
                             <StateBadge state={tripState} stateName={stateName} withIcon={true} />
                      </Div>
                      {/* El viaje salio y, con la ruta recalculada a la hora real de salida, su
                          ultima entrega cae despues del cierre de la jornada. Lo calcula el
                          backend (Trip#workday_overrun) y solo viene para viajes en curso de hoy. */}
                      {canSeeWorkdayOverrun && trip.workday_overrun && tripState === 'in_progress' && (
                          <Alert
                              severity="warning"
                              icon={<MoreTimeOutlinedIcon fontSize="small" />}
                              sx={{ mt: 1, py: 0, fontSize: 12, '& .MuiAlert-icon': { py: 0.75 } }}
                          >
                              Según la planificación termina a las <strong>{moment(trip.workday_overrun.ends_at).format('HH:mm')}</strong>:
                              {' '}se pasa {formatOverMinutes(trip.workday_overrun.over_minutes)} de la jornada
                              {' '}({moment(trip.workday_overrun.workday_end).format('HH:mm')}).
                          </Alert>
                      )}
                      <Grid container spacing={2}>
                          <Grid item xs={12} sm={8} md={8} lg={8} xl={8} py={0}>
                              <Div sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Grid container spacing={2} alignItems="center">
                                      <Grid item xs={12} sm={8} md={8} lg={8} xl={8}>
                                          <Div sx={{ width: '100%', mr: 1 }}>
                                              <LinearProgress
                                                  variant="determinate"
                                                  color={stateInfo(trip.state).color}
                                                  value={(trip.deliveries_ended / trip.deliveries_total) * 100}
                                              />
                                          </Div>
                                      </Grid>
                                      <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
                                          <Div sx={{ minWidth: 35 }}>
                                              <Typography variant="body2" color="text.secondary">
                                                  {`${trip.deliveries_ended}/${trip.deliveries_total}`}
                                              </Typography>
                                          </Div>
                                      </Grid>
                                  </Grid>
                              </Div>
                              {authUser?.current_company?.id === 80 && (
                                  <Div sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                                      <Grid container spacing={2} alignItems="center">
                                          <Grid item xs={12} sm={8} md={8} lg={8} xl={8}>
                                              <Div sx={{ width: '100%', mr: 1 }}>
                                                  <LinearProgress
                                                      variant="determinate"
                                                      value={(trip.pending_qty / trip.max_load_volume) * 100}
                                                      sx={{
                                                          backgroundColor: '#d8cfcf', // A lighter shade of #322C2B
                                                          '& .MuiLinearProgress-bar': {
                                                              backgroundColor: '#5b5655',
                                                          },
                                                      }}
                                                  />
                                              </Div>
                                          </Grid>
                                          <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
                                              <Div sx={{ minWidth: 35 }}>
                                                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                                      {`${trip.pending_qty.toLocaleString()}/${trip.max_load_volume.toLocaleString()} L`}
                                                  </Typography>
                                              </Div>
                                          </Grid>
                                      </Grid>
                                  </Div>
                              )}
                          </Grid>
                      </Grid>

                  </Grid>

                  <Grid item xs={12} md={6}>
                      {renderDeliveries()}
                  </Grid>

                  <Grid item xs={12} md={3} sx={{ textAlign: 'right' }} pr={1}>
                      {trip.order_deviation && (() => {
                          // Naranjo: el chofer cambio el orden desde la app. Rojo: cerro las
                          // entregas en otro orden que el planificado (manda sobre el naranjo).
                          const offPlan = trip.order_deviation.kind === 'off_plan';
                          const title = offPlan
                              ? (trip.order_deviation.app_reorder
                                  ? 'El conductor cambió el orden desde la app móvil y además siguió un orden diferente al planificado'
                                  : 'El conductor siguió un orden diferente al planificado')
                              : 'El conductor cambió el orden de entrega desde la app móvil';
                          const params = new URLSearchParams({ panel: 'km_planificado' });
                          if (trip.driver?.id) params.set('driver_id', trip.driver.id);
                          if (trip.ship_date) params.set('mes', moment(trip.ship_date).format('YYYY-MM'));
                          return (
                              <Tooltip title={title}>
                                  <IconButton edge="end" onClick={() => navigate(`/business-dashboard?${params.toString()}`)}>
                                      {/* fa-road de Font Awesome (se carga en public/index.html). El ancho fijo lo
                                          deja del mismo tamaño que los iconos de MUI de al lado. */}
                                      <Box
                                          component="i"
                                          className="fa-solid fa-road"
                                          sx={{ fontSize: 20, width: 24, textAlign: 'center', color: offPlan ? 'error.main' : 'warning.main' }}
                                      />
                                  </IconButton>
                              </Tooltip>
                          );
                      })()}
                      {labelSummary?.total > 0 && (
                          <Tooltip title={labelSummary.pending > 0
                              ? `${labelSummary.pending} de ${labelSummary.total} etiquetas por imprimir`
                              : 'Todas las etiquetas del viaje están impresas'}>
                              <IconButton edge="end" onClick={() => navigate(`/etiquetas?trip_id=${trip.id}`)}>
                                  <Badge badgeContent={labelSummary.pending} color="warning" max={99}>
                                      <QrCode2Icon color={labelSummary.pending > 0 ? 'warning' : 'success'} />
                                  </Badge>
                              </IconButton>
                          </Tooltip>
                      )}
                      {canCloseTrip && (
                          <Tooltip title="Cerrar viaje">
                              <span>
                                  <IconButton edge="end" onClick={() => setConfirmCloseTrip(true)} disabled={closingTrip}>
                                      {closingTrip ? <CircularProgress size={20} /> : <TaskAltOutlinedIcon color="success" />}
                                  </IconButton>
                              </span>
                          </Tooltip>
                      )}
                      <Tooltip title={trip.state === 'planned' ? 'El mapa se habilita cuando el viaje deja de estar planificado' : 'Ver mapa y ruta del viaje'}>
                          <span>
                              <IconButton edge="end" onClick={() => onTripFocus(trip)} disabled={trip.state === 'planned'}>
                                  <MapOutlinedIcon color={_.get(trip, 'deliveries[0].has_route') === true ? 'success' : 'disabled'} />
                              </IconButton>
                          </span>
                      </Tooltip>
                      {authUser?.current_company?.id !== 80 && (
                        <>
                            <Tooltip title={driverContactTooltip}>
                                <span>
                                <IconButton edge="end" onClick={() => onDriverEdit(trip)}>
                                    {hasDriverPhone ?
                                        <PhoneIphoneOutlinedIcon color='success' />
                                        :
                                        <PhonelinkEraseOutlinedIcon color='error' />
                                    }
                                </IconButton>
                                </span>
                            </Tooltip>
                        </>
                      )}
                      <Tooltip title={tripImagesCount > 0 ? 'Descargar imagenes del viaje' : 'Sin imagenes para descargar'}>
                          <span>
                              <ImagesDownloadBtn fieldIdentifiers="trip.deliveries.delivery_details.images" resourceIds={trip.id} disabled={tripImagesCount <= 0} />
                          </span>
                      </Tooltip>
                      <Tooltip title={viewMoreActive ? 'Ver menos entregas' : 'Ver mas entregas'}>
                          <IconButton edge="end" onClick={() => onViewMore(trip)}>
                              {viewMoreActive ? <UnfoldLessOutlinedIcon /> : <UnfoldMoreOutlinedIcon />}
                          </IconButton>
                      </Tooltip>
                  </Grid>
              </Grid>
          </CardContent>
      </Card>

      <ConfirmDialog
          open={confirmCloseTrip}
          title="Cerrar viaje"
          message={
              <>
                  El viaje <strong>{trip.code}</strong> quedará Finalizado.
                  {openDeliveriesCount > 0 && (
                      <>
                          <br />
                          {openDeliveriesCount === 1
                              ? 'La parada que sigue abierta queda entregada y su pedido Finalizado.'
                              : `Las ${openDeliveriesCount} paradas que siguen abiertas quedan entregadas y sus pedidos Finalizados.`}
                      </>
                  )}
              </>
          }
          confirmText="Cerrar viaje"
          confirmColor="success"
          loading={closingTrip}
          onClose={() => setConfirmCloseTrip(false)}
          onConfirm={closeTrip}
      />

      <ConfirmDialog
          open={Boolean(confirmFinishDelivery)}
          title="Finalizar pedido"
          message={
              <>
                  El pedido <strong>{confirmFinishDelivery?.order?.code}</strong> quedará Finalizado y su parada entregada.
                  {openDeliveriesCount === 1 && (
                      <>
                          <br />
                          Es la última parada abierta: el viaje <strong>{trip.code}</strong> se cierra.
                      </>
                  )}
              </>
          }
          confirmText="Finalizar"
          confirmColor="success"
          loading={Boolean(confirmFinishDelivery) && finishingDeliveryId === confirmFinishDelivery?.id}
          onClose={() => setConfirmFinishDelivery(null)}
          onConfirm={() => finishDelivery(confirmFinishDelivery)}
      />

      <ConfirmDialog
          open={Boolean(confirmCancelDelivery)}
          title="Cancelar pedido en viaje"
          message={
              <>
                  ¿Estás seguro que deseas cancelar {confirmCancelDelivery?.order?.code ? <>el pedido <strong>{confirmCancelDelivery.order.code}</strong></> : <>la entrega <strong>{confirmCancelDelivery?.code}</strong></>} del viaje <strong>{trip.code}</strong>?
                  <br />
                  Esta acción no se puede deshacer.
              </>
          }
          confirmText="Cancelar pedido"
          loading={cancellingDeliveryId === confirmCancelDelivery?.id}
          onClose={() => setConfirmCancelDelivery(null)}
          onConfirm={() => {
              const delivery = confirmCancelDelivery;
              setConfirmCancelDelivery(null);
              cancelOrderInTrip(delivery);
          }}
      />
      </>
  );
};

export default TripListCard;
